import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { generateProposalPDF } from '../../utils/generateProposalPDF';
import * as XLSX from 'xlsx';
import API_URL from '../../config/api';

const supabase = createClient('https://cvytfjkefrrfibbbstig.supabase.co', process.env.REACT_APP_SUPABASE_ANON_KEY || 'sb_secret_CqiRDhHfw4Rg2e9ECdgmzA_mB85NFhR');

// Helper to format BRL consistently
const formatBRL = (value) => {
  const num = typeof value === 'number' && isFinite(value) ? value : 0;
  return `R$ ${num.toFixed(2).replace('.', ',')}`;
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const QuotationsConsultar = () => {
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openActionId, setOpenActionId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const actionMenuRef = useRef(null); // Ref for the currently open action menu container
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadedAttachments, setUploadedAttachments] = useState([]); // New state for uploaded attachments
  const [fileType, setFileType] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewQuotationData, setViewQuotationData] = useState(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [quotationToCancel, setQuotationToCancel] = useState(null);

  // Search filters state
  const [searchFilters, setSearchFilters] = useState({
    numero: '',
    versao: 'Atual',
    dataInicio: '',
    dataFim: '',
    filial: '',
    setor: '',
    status: '',
    vendedor: '',
    cliente: ''
  });

  const [filiais, setFiliais] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const navigate = useNavigate();

  // Fetch quotations data from API or backend
  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        const response = await fetch(`${API_URL}/api/quotations`);
        if (!response.ok) {
          throw new Error('Failed to fetch quotations');
        }
        const result = await response.json();
        const allQuotations = result.quotations || [];
        setQuotations(allQuotations);
        
        // Por padrão, mostrar orçamentos do mais novo para o mais antigo
        const latestVersions = {};
        allQuotations.forEach(q => {
          const id = q.quotation_id;
          if (!latestVersions[id] || q.version > latestVersions[id].version) {
            latestVersions[id] = q;
          }
        });
        const sortedLatestVersions = Object.values(latestVersions).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setFilteredQuotations(sortedLatestVersions);
      } catch (error) {
        console.error('Error fetching quotations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotations();
  }, []);

  // Fetch filiais and usuarios for filters
  useEffect(() => {
    const fetchFiliais = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/filiais`);
        const data = await response.json();
        setFiliais(data.filiais || []);
      } catch (error) {
        console.error('Error fetching filiais:', error);
      }
    };

    const fetchUsuarios = async () => {
      try {
        const response = await fetch(`${API_URL}/api/usuarios`);
        const data = await response.json();
        setUsuarios(data.usuarios || []);
      } catch (error) {
        console.error('Error fetching usuarios:', error);
      }
    };

    fetchFiliais();
    fetchUsuarios();
  }, []);

  // Add outside click handler to close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If no menu is open, do nothing
      if (openActionId === null) return;

      // Check if click is inside the button or menu for the openActionId
      const button = document.getElementById(`action-button-${openActionId}`);
      const menu = document.getElementById(`action-menu-${openActionId}`);

      if (button && button.contains(event.target)) {
        // Click inside button, toggle handled by button click
        return;
      }
      if (menu && menu.contains(event.target)) {
        // Click inside menu, do nothing
        return;
      }
      // Click outside, close menu
      setOpenActionId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const toggleActionMenu = (uniqueId, event) => {
    if (openActionId === uniqueId) {
      setOpenActionId(null);
      return;
    }
    
    // Calcular posição do menu baseado no botão clicado
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    
    // Posicionar o menu abaixo e alinhado à direita do botão
    const menuWidth = 60; // largura do menu (apenas ícones)
    const menuHeight = 300; // altura aproximada do menu
    
    let top = rect.bottom + window.scrollY + 5;
    let left = rect.right + window.scrollX - menuWidth;
    
    // Verificar se o menu sairia da tela pela direita
    if (left + menuWidth > window.innerWidth) {
      left = rect.left + window.scrollX - menuWidth - 5;
    }
    
    // Verificar se o menu sairia da tela pela esquerda
    if (left < 0) {
      left = rect.left + window.scrollX;
    }
    
    // Verificar se o menu sairia da tela por baixo
    if (rect.bottom + menuHeight > window.innerHeight) {
      top = rect.top + window.scrollY - menuHeight - 5;
    }
    
    setMenuPosition({ top, left });
    setOpenActionId(uniqueId);
  };

  // Placeholder action handlers
  const handleView = (quotation) => {
    // Set the entire quotation object to viewQuotationData for full access
    setViewQuotationData(quotation);
    setIsViewModalOpen(true);
  };

  const handleEdit = (quotation) => {
    // Navigate to edit page with quotation id and version as params or state
    navigate(`/admin/quotations/edit`, { state: { quotation } });
  };

  const handlePrint = async (quotation) => {
    try {
      setLoading(true);
      
      // Buscar dados da filial
      const filialCode = quotation.data?.filial;
      let filialData = null;
      
      if (filialCode) {
        const response = await fetch(`${API_URL}/api/admin/filiais`);
        if (response.ok) {
          const data = await response.json();
          filialData = data.filiais?.find(f => f.filial === filialCode || f.nome === filialCode);
        }
      }
      
      // Buscar dados do cliente
      let clientData = null;
      const clienteField = quotation.data?.cliente;
      
      if (clienteField) {
        // Extrair código do cliente (formato: "000001 - Nome do Cliente")
        const clientCodeMatch = clienteField.match(/^(\d+)/);
        if (clientCodeMatch) {
          const clientCode = clientCodeMatch[1];
          const clientResponse = await fetch(`${API_URL}/api/clients?search=${clientCode}`);
          if (clientResponse.ok) {
            const clientsData = await clientResponse.json();
            clientData = clientsData.clients?.find(c => c.code === clientCode);
          }
        }
      }
      
      // Buscar celular do vendedor da tabela usuarios
      const vendedorNome = quotation.data?.vendedor;
      let celularVendedor = '';
      
      if (vendedorNome) {
        try {
          const usuariosResponse = await fetch(`${API_URL}/api/usuarios`);
          if (usuariosResponse.ok) {
            const usuariosData = await usuariosResponse.json();
            console.log('Todos os usuários:', usuariosData.usuarios);
            console.log('Procurando vendedor:', vendedorNome);
            
            // Tentar encontrar por diferentes campos
            const vendedor = usuariosData.usuarios?.find(u => 
              u.name === vendedorNome || 
              u.nome === vendedorNome ||
              u.username === vendedorNome ||
              (u.code && vendedorNome.includes(u.code))
            );
            
            console.log('Vendedor encontrado:', vendedor);
            
            if (vendedor) {
              celularVendedor = vendedor.celular || vendedor.telefone || '';
              console.log('Celular do vendedor:', celularVendedor);
            } else {
              console.warn('Vendedor não encontrado na tabela usuarios');
            }
          }
        } catch (error) {
          console.error('Erro ao buscar celular do vendedor:', error);
        }
      }
      
      // Adicionar celular do vendedor ao formData
      const quotationWithVendedor = {
        ...quotation,
        data: {
          ...quotation.data,
          celularVendedor: celularVendedor
        }
      };
      
      // Buscar cláusulas ativas
      const { data: clausulas, error: clausulasError } = await supabase
        .from('clausulas')
        .select('*')
        .eq('ativa', true)
        .order('id', { ascending: true });
      
      if (clausulasError) {
        console.error('Erro ao buscar cláusulas:', clausulasError);
      }
      
      // Gerar PDF
      await generateProposalPDF(quotationWithVendedor, filialData, clausulas || [], clientData);
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  const handleAttach = (quotation) => {
    setSelectedQuotation(quotation);
    setIsModalOpen(true);
    fetchUploadedAttachments(quotation.quotation_id); // Fetch uploaded attachments when modal opens
    // Clear form fields when modal opens
    setDescription('');
    setFileType('');
    setSelectedFile(null);
  };
  const handleConvert = async (quotation) => {
    try {
      setLoading(true);
      
      // Verificar se existe anexo do tipo "Pedido Assinado ou Ordem de Compra"
      const response = await fetch(`${API_URL}/api/attachments/${quotation.quotation_id}`);
      if (!response.ok) {
        throw new Error('Erro ao verificar anexos');
      }
      
      const result = await response.json();
      const attachments = result.attachments || [];
      
      const hasPedidoAssinado = attachments.some(att => 
        att.tipo_arquivo === 'Pedido Assinado ou Ordem de Compra'
      );
      
      if (!hasPedidoAssinado) {
        alert('Para converter em pedido, é necessário anexar um documento do tipo "Pedido Assinado ou Ordem de Compra"');
        setLoading(false);
        return;
      }
      
      // Atualizar status para "Pendente Aprovação"
      const statusResponse = await fetch(
        `${API_URL}/api/quotations/${quotation.quotation_id}/${quotation.version || 1}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: 'Pendente Aprovação' }),
        }
      );
      
      if (!statusResponse.ok) {
        throw new Error('Erro ao atualizar status');
      }
      
      alert('Pedido enviado para aprovação com sucesso!');
      
      // Atualizar lista de orçamentos
      const quotationsResponse = await fetch(`${API_URL}/api/quotations`);
      if (quotationsResponse.ok) {
        const quotationsData = await quotationsResponse.json();
        const allQuotations = quotationsData.quotations || [];
        setQuotations(allQuotations);
        
        // Reaplica filtro de versão atual
        const latestVersions = {};
        allQuotations.forEach(q => {
          const id = q.quotation_id;
          if (!latestVersions[id] || q.version > latestVersions[id].version) {
            latestVersions[id] = q;
          }
        });
        setFilteredQuotations(Object.values(latestVersions));
      }
      
    } catch (error) {
      console.error('Erro ao converter pedido:', error);
      alert('Erro ao converter pedido: ' + error.message);
    } finally {
      setLoading(false);
      setOpenActionId(null);
    }
  };
  
  const handleCancel = (quotation) => {
    setQuotationToCancel(quotation);
    setIsCancelModalOpen(true);
    setOpenActionId(null);
  };

  const handleCancelConfirm = async () => {
    if (!cancelReason.trim()) {
      alert('Por favor, informe o motivo da não venda');
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch(
        `${API_URL}/api/quotations/${quotationToCancel.quotation_id}/${quotationToCancel.version || 1}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            status: 'Cancelado',
            motivoCancelamento: cancelReason
          }),
        }
      );
      
      if (!response.ok) {
        throw new Error('Erro ao cancelar orçamento');
      }
      
      alert('Orçamento cancelado com sucesso!');
      
      // Atualizar lista
      const quotationsResponse = await fetch(`${API_URL}/api/quotations`);
      if (quotationsResponse.ok) {
        const quotationsData = await quotationsResponse.json();
        const allQuotations = quotationsData.quotations || [];
        setQuotations(allQuotations);
        
        const latestVersions = {};
        allQuotations.forEach(q => {
          const id = q.quotation_id;
          if (!latestVersions[id] || q.version > latestVersions[id].version) {
            latestVersions[id] = q;
          }
        });
        setFilteredQuotations(Object.values(latestVersions));
      }
      
      // Fechar modal e limpar
      setIsCancelModalOpen(false);
      setCancelReason('');
      setQuotationToCancel(null);
      
    } catch (error) {
      console.error('Erro ao cancelar:', error);
      alert('Erro ao cancelar orçamento: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelModalClose = () => {
    setIsCancelModalOpen(false);
    setCancelReason('');
    setQuotationToCancel(null);
  };

  const fetchUploadedAttachments = async (quotationId) => {
    try {
      console.log('Fetching uploaded attachments for quotation:', quotationId);
      const response = await fetch(`${API_URL}/api/attachments/${quotationId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch attachments');
      }
      const result = await response.json();
      console.log('Fetched attachments:', result.attachments);
      setUploadedAttachments(result.attachments || []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setUploadedAttachments([]);
    }
  };



  const handleAddAttachment = () => {
    if (!description.trim() || !selectedFile || !fileType) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      alert('Arquivo muito grande, máximo 5MB');
      return;
    }
    setAttachments([...attachments, { description, fileType, file: selectedFile, date: new Date().toLocaleDateString() }]);
    setDescription('');
    setFileType('');
    setSelectedFile(null);
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleConcluir = async () => {
    if (!selectedQuotation || !selectedQuotation.quotation_id) {
      alert('Nenhum orçamento selecionado para anexar documentos.');
      return;
    }
    for (const attachment of attachments) {
      const fileName = attachment.file.name;
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(attachment.file);
      });
      const contentType = attachment.file.type;

      try {
        const response = await fetch(`${API_URL}/api/upload-attachment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            quotationId: selectedQuotation.quotation_id,
            fileName,
            fileBase64,
            contentType,
            tipoArquivo: attachment.fileType,
            descricao: attachment.description,
          }),
        });
        if (!response.ok) {
          throw new Error('Erro no upload');
        }
        const result = await response.json();
        console.log('Upload successful:', result);
      } catch (error) {
        console.error('Error uploading file:', error);
        alert('Erro ao fazer upload do arquivo: ' + fileName);
        return;
      }
    }
    alert('Arquivos enviados com sucesso');
    setAttachments([]);
    await fetchUploadedAttachments(selectedQuotation.quotation_id); // Refresh uploaded attachments list after upload
    // Clear form fields after successful upload
    setDescription('');
    setFileType('');
    setSelectedFile(null);
    // Do not close modal automatically
  };

  const handleSearchFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyFilters = () => {
    let filtered = [...quotations];

    // Filter by numero
    if (searchFilters.numero) {
      filtered = filtered.filter(q => 
        q.quotation_id.toString().includes(searchFilters.numero)
      );
    }

    // Filter by versao
    if (searchFilters.versao === 'Atual') {
      // Group by quotation_id and keep only the latest version
      const latestVersions = {};
      filtered.forEach(q => {
        const id = q.quotation_id;
        if (!latestVersions[id] || q.version > latestVersions[id].version) {
          latestVersions[id] = q;
        }
      });
      filtered = Object.values(latestVersions);
    }
    // If 'Todas', show all versions (no filtering needed)

    // Filter by date range
    if (searchFilters.dataInicio) {
      const startDate = new Date(searchFilters.dataInicio + 'T00:00:00');
      filtered = filtered.filter(q => new Date(q.created_at) >= startDate);
    }
    if (searchFilters.dataFim) {
      const endDate = new Date(searchFilters.dataFim + 'T23:59:59.999');
      filtered = filtered.filter(q => new Date(q.created_at) <= endDate);
    }

    // Filter by filial
    if (searchFilters.filial) {
      filtered = filtered.filter(q => 
        q.data?.filial?.toLowerCase().includes(searchFilters.filial.toLowerCase())
      );
    }

    // Filter by setor
    if (searchFilters.setor) {
      filtered = filtered.filter(q => 
        q.data?.setor?.toLowerCase().includes(searchFilters.setor.toLowerCase())
      );
    }

    // Filter by status (placeholder - adjust based on your status logic)
    if (searchFilters.status) {
      // For now, all quotations have status "Em Negociação"
      // You can extend this logic based on actual status field
      filtered = filtered.filter(q => {
        // Add your status logic here
        return true; // Placeholder
      });
    }

    // Filter by vendedor
    if (searchFilters.vendedor) {
      filtered = filtered.filter(q => 
        q.data?.vendedor?.toLowerCase().includes(searchFilters.vendedor.toLowerCase())
      );
    }

    // Filter by cliente
    if (searchFilters.cliente) {
      filtered = filtered.filter(q => 
        q.data?.cliente?.toLowerCase().includes(searchFilters.cliente.toLowerCase())
      );
    }

    setFilteredQuotations(filtered);
    setIsSearchModalOpen(false);
  };

  const clearFilters = () => {
    setSearchFilters({
      numero: '',
      versao: 'Atual',
      dataInicio: '',
      dataFim: '',
      filial: '',
      setor: '',
      status: '',
      vendedor: '',
      cliente: ''
    });
    // Reaplica filtro de versão atual ao limpar
    const latestVersions = {};
    quotations.forEach(q => {
      const id = q.quotation_id;
      if (!latestVersions[id] || q.version > latestVersions[id].version) {
        latestVersions[id] = q;
      }
    });
    setFilteredQuotations(Object.values(latestVersions));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredQuotations].sort((a, b) => {
      let aValue, bValue;

      switch (key) {
        case 'numero':
          aValue = a.quotation_id;
          bValue = b.quotation_id;
          break;
        case 'filial':
          aValue = (a.data?.filial || '').toLowerCase();
          bValue = (b.data?.filial || '').toLowerCase();
          break;
        case 'cliente':
          aValue = (a.data?.cliente || '').toLowerCase();
          bValue = (b.data?.cliente || '').toLowerCase();
          break;
        case 'vendedor':
          aValue = (a.data?.vendedor || '').toLowerCase();
          bValue = (b.data?.vendedor || '').toLowerCase();
          break;
        case 'over':
          aValue = Number(a.data?.totalOver) || 0;
          bValue = Number(b.data?.totalOver) || 0;
          break;
        case 'valor':
          aValue = Number(a.data?.valorTotalPedido) || 0;
          bValue = Number(b.data?.valorTotalPedido) || 0;
          break;
        case 'data':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'status':
          // Ordem de prioridade: Em Negociação < Pendente Aprovação < Importado < Cancelado
          const statusOrder = {
            'Em Negociação': 1,
            'Pendente Aprovação': 2,
            'Importado': 3,
            'Cancelado': 4
          };
          aValue = statusOrder[a.status || 'Em Negociação'] || 0;
          bValue = statusOrder[b.status || 'Em Negociação'] || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredQuotations(sorted);
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline ml-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline ml-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredQuotations.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredQuotations.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleExportToExcel = () => {
    // Prepare data for export
    const dataToExport = filteredQuotations.map(q => ({
      'Número': `${q.quotation_id}.${q.version || 1}`,
      'Filial': q.data?.filial || '',
      'Cliente': q.data?.cliente || '',
      'Vendedor': q.data?.vendedor || '',
      'Indicador': Array.isArray(q.data?.indicadores) && q.data.indicadores.length > 0 
        ? q.data.indicadores.map(ind => {
            const totalOver = Number(q.data?.totalOver) || 0;
            const v = parseFloat(ind.valor);
            const valorNum = isNaN(v) ? 0 : v;
            const valorBRL = ind.valorTipo === '%' ? (totalOver * (valorNum / 100)) : valorNum;
            return `${ind.nome} ${formatBRL(valorBRL)}`;
          }).join('; ')
        : '',
      'Over': q.data?.totalOver ? `R$ ${Number(q.data.totalOver).toFixed(2)}` : '',
      'Valor': q.data?.valorTotalPedido ? `R$ ${Number(q.data.valorTotalPedido).toFixed(2)}` : '',
      'Data': new Date(q.created_at).toLocaleDateString('pt-BR'),
      'Status': q.status || 'Em Negociação'
    }));

    // Create worksheet from data
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Set column widths
    const columnWidths = [
      { wch: 12 }, // Número
      { wch: 20 }, // Filial
      { wch: 30 }, // Cliente
      { wch: 25 }, // Vendedor
      { wch: 30 }, // Indicador
      { wch: 15 }, // Over
      { wch: 15 }, // Valor
      { wch: 12 }, // Data
      { wch: 20 }, // Status
    ];
    worksheet['!cols'] = columnWidths;

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orçamentos');

    // Generate filename with current date
    const fileName = `orcamentos_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="p-6 bg-white rounded shadow">
      {/* Header with title and controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <h1 className="text-xl font-semibold">Dados para consulta</h1>
        <div className="flex flex-wrap items-center gap-3">
          {/* Export Button */}
          <button
            onClick={handleExportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Exportar</span>
          </button>
          {/* Search Button */}
          <button
            onClick={() => setIsSearchModalOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
          >
            <span>Pesquisar</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </div>
      </div>

      {loading ? (
        <p>Carregando orçamentos...</p>
      ) : (
        <>
          {/* Pagination Controls - Top */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Itens por página</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
              </select>
              <span className="text-sm text-gray-600">
                {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredQuotations.length)} de {filteredQuotations.length}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th 
                  className="border px-2 sm:px-4 py-2 text-center cursor-pointer hover:bg-gray-200 select-none text-xs sm:text-sm"
                  onClick={() => handleSort('numero')}
                >
                  Número <SortIcon columnKey="numero" />
                </th>
                <th 
                  className="border px-2 sm:px-4 py-2 text-center cursor-pointer hover:bg-gray-200 select-none text-xs sm:text-sm hidden md:table-cell"
                  onClick={() => handleSort('filial')}
                >
                  Filial <SortIcon columnKey="filial" />
                </th>
                <th 
                  className="border px-2 sm:px-4 py-2 text-center cursor-pointer hover:bg-gray-200 select-none text-xs sm:text-sm"
                  onClick={() => handleSort('cliente')}
                >
                  Cliente <SortIcon columnKey="cliente" />
                </th>
                <th 
                  className="border px-2 sm:px-4 py-2 text-center cursor-pointer hover:bg-gray-200 select-none text-xs sm:text-sm hidden lg:table-cell"
                  onClick={() => handleSort('vendedor')}
                >
                  Vendedor <SortIcon columnKey="vendedor" />
                </th>
                <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm hidden xl:table-cell">Indicador</th>
                <th 
                  className="border px-2 sm:px-4 py-2 text-center cursor-pointer hover:bg-gray-200 select-none text-xs sm:text-sm hidden md:table-cell"
                  onClick={() => handleSort('valor')}
                >
                  Valor <SortIcon columnKey="valor" />
                </th>
                <th 
                  className="border px-2 sm:px-4 py-2 text-center cursor-pointer hover:bg-gray-200 select-none text-xs sm:text-sm hidden xl:table-cell"
                  onClick={() => handleSort('over')}
                >
                  Over <SortIcon columnKey="over" />
                </th>
                <th 
                  className="border px-2 sm:px-4 py-2 text-center cursor-pointer hover:bg-gray-200 select-none text-xs sm:text-sm hidden md:table-cell"
                  onClick={() => handleSort('data')}
                >
                  Data <SortIcon columnKey="data" />
                </th>
                <th 
                  className="border px-2 sm:px-4 py-2 text-center cursor-pointer hover:bg-gray-200 select-none text-xs sm:text-sm"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex justify-center items-center">
                    Status <SortIcon columnKey="status" />
                  </div>
                </th>
                <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Ações</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center p-4">Nenhum orçamento encontrado.</td>
                </tr>
              ) : (
                currentItems.map((q) => {
                  const uniqueId = `${q.quotation_id}-${q.version || 1}`;
                  return (
                  <tr key={uniqueId} className="hover:bg-gray-50 relative">
                    <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm">{q.quotation_id}.{q.version || 1}</td>
                    <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm hidden md:table-cell">{q.data?.filial}</td>
                    <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm">{q.data?.cliente}</td>
                    <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm hidden lg:table-cell">{q.data?.vendedor}</td>
                    <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm hidden xl:table-cell">
                      {Array.isArray(q.data?.indicadores) && q.data.indicadores.length > 0 ? (
                        (() => {
                          const totalOver = Number(q.data?.totalOver) || 0;
                          let descontoAmount = 0;
                          if (q.data?.desconto && q.data.desconto > 0) {
                            if (q.data.descontoType === '%') {
                              descontoAmount = totalOver * (parseFloat(q.data.desconto) / 100);
                            } else {
                              descontoAmount = parseFloat(q.data.desconto) || 0;
                            }
                          }
                          const totalOverAfterDiscount = totalOver - descontoAmount;
                          const baseForIndicator = totalOverAfterDiscount + parseFloat(q.data?.ajusteFrete || 0);
                          return (
                            <div className="flex flex-col divide-y divide-gray-300">
                              {q.data.indicadores.map((ind, idx) => {
                                const v = parseFloat(ind.valor);
                                const valorNum = isNaN(v) ? 0 : v;
                                const valorBRL = ind.valorTipo === '%' ? (baseForIndicator * (valorNum / 100)) : valorNum;
                                return (
                                  <div key={idx} className="py-0.5">
                                    {ind.nome} {formatBRL(valorBRL)}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()
                      ) : ''}
                    </td>
                    <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm font-semibold hidden md:table-cell">
                      {(() => {
                        const itemsTotal = q.data?.items ? q.data.items.reduce((sum, item) => sum + (item.valorTotal || 0), 0) : 0;
                        const freightToAdd = q.data?.tipoFrete === 'CIF' ? (q.data.freteDestacadoCalculado || 0) : 0;

                        // Calculate discount
                        const totalOver = q.data?.items ? q.data.items.reduce((sum, item) => {
                          const valorTabela = item.valorTabela || 0;
                          const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
                          return sum + over * item.quantidade;
                        }, 0) : 0;

                        let descontoAmount = 0;
                        if (q.data?.desconto && q.data.desconto > 0) {
                          if (q.data.descontoType === '%') {
                            descontoAmount = totalOver * (parseFloat(q.data.desconto) / 100);
                          } else {
                            descontoAmount = parseFloat(q.data.desconto) || 0;
                          }
                        }

                        const valorTotalComDesconto = itemsTotal + freightToAdd - descontoAmount;
                        return `R$ ${valorTotalComDesconto.toFixed(2)}`;
                      })()}
                    </td>
                    <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm hidden xl:table-cell">
                      {(() => {
                        const totalOver = Number(q.data?.totalOver) || 0;
                        let descontoAmount = 0;
                        if (q.data?.desconto && q.data.desconto > 0) {
                          if (q.data.descontoType === '%') {
                            descontoAmount = totalOver * (parseFloat(q.data.desconto) / 100);
                          } else {
                            descontoAmount = parseFloat(q.data.desconto) || 0;
                          }
                        }
                        const totalOverAfterDiscount = totalOver - descontoAmount;
                        const netOver = totalOverAfterDiscount + parseFloat(q.data?.ajusteFrete || 0);
                        return `R$ ${netOver.toFixed(2)}`;
                      })()}
                    </td>
                    <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm hidden md:table-cell">{new Date(q.created_at).toLocaleDateString()}</td>
                    <td className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">
                      <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${
                        q.status === 'Pendente Aprovação' ? 'bg-orange-100 text-orange-700' :
                        q.status === 'Importado' ? 'bg-green-100 text-green-700' :
                        q.status === 'Cancelado' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {q.status || 'Em Negociação'}
                      </span>
                    </td>
                    <td className="border px-2 sm:px-4 py-2">
                      <button
                        id={`action-button-${uniqueId}`}
                        onClick={(e) => toggleActionMenu(uniqueId, e)}
                        className="p-1 rounded hover:bg-gray-200"
                        aria-label="Ações"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

          {/* Pagination Controls - Bottom */}
          {totalPages > 1 && (
            <div className="flex flex-wrap justify-center items-center mt-4 gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                // Show first page, last page, current page, and pages around current
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-3 py-1 border rounded-md ${
                        currentPage === pageNumber
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return <span key={pageNumber} className="px-2">...</span>;
                }
                return null;
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}

      {/* Action Menu - Renderizado fora da tabela com position fixed */}
      {openActionId && (
        <div
          id={`action-menu-${openActionId}`}
          className="fixed bg-white border border-gray-300 rounded shadow-lg z-[9999] p-2 flex flex-col gap-2 min-w-[48px]"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
        >
          {(() => {
            const q = currentItems.find(item => `${item.quotation_id}-${item.version || 1}` === openActionId);
            if (!q) return null;
            
            return (
              <>
                <button
                  onClick={() => handleView(q)}
                  title="Visualizar"
                  className="p-1 rounded hover:bg-gray-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleEdit(q)}
                  title="Editar"
                  className="p-1 rounded hover:bg-gray-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handlePrint(q)}
                  title="Imprimir proposta"
                  className="p-1 rounded hover:bg-gray-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9v6h12V9M6 9V7a2 2 0 012-2h8a2 2 0 012 2v2M6 15v2a2 2 0 002 2h8a2 2 0 002-2v-2" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 14h12" />
                  </svg>
                </button>
                <button
                  onClick={() => handleAttach(q)}
                  title="Anexar documentos ao orçamento"
                  className="p-1 rounded hover:bg-gray-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828L18 9.828a4 4 0 00-5.656-5.656L7 10.172a6 6 0 008.485 8.485l3.586-3.586" />
                  </svg>
                </button>
                {(!q.status || q.status === 'Em Negociação') && (
                  <>
                    <button
                      onClick={() => handleConvert(q)}
                      title="Converter em pedido"
                      className="p-1 rounded hover:bg-gray-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m1 5a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleCancel(q)}
                      title="Indicar não venda / Cancelar"
                      className="p-1 rounded hover:bg-gray-200 text-red-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Anexar Documentos - {selectedQuotation.quotation_id}.{selectedQuotation.version || 1}</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Arquivo</label>
              <select value={fileType} onChange={(e) => setFileType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="">Selecione</option>
                <option value="Pedido Assinado ou Ordem de Compra">Pedido Assinado ou Ordem de Compra</option>
                <option value="Comprovante de Pagamento">Comprovante de Pagamento</option>
                <option value="Fotos">Fotos</option>
                <option value="Documentos">Documentos</option>
                <option value="Outros">Outros</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Descrição *</label>
              <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Arquivo</label>
              <input type="file" onChange={(e) => setSelectedFile(e.target.files[0])} accept="*" className="w-full" />
              {selectedFile && <p className="mt-2 text-sm text-gray-600">{selectedFile.name}</p>}
            </div>
            <button onClick={handleAddAttachment} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 mb-4">+ Adicionar</button>
            <table className="min-w-full border border-gray-300 mb-4">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-4 py-2">Descrição</th>
                  <th className="border px-4 py-2">Tipo</th>
                  <th className="border px-4 py-2">Data</th>
                  <th className="border px-4 py-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {/* Display newly added attachments */}
                {attachments.map((att, index) => (
                  <tr key={`new-${index}`}>
                    <td className="border px-4 py-2">{att.description}</td>
                    <td className="border px-4 py-2">{att.fileType}</td>
                    <td className="border px-4 py-2">{att.date}</td>
                    <td className="border px-4 py-2">
                      <button onClick={() => window.open(URL.createObjectURL(att.file))} className="text-blue-600 mr-2">Ver</button>
                      <button onClick={() => handleRemoveAttachment(index)} className="text-red-600">Excluir</button>
                    </td>
                  </tr>
                ))}
                {/* Display uploaded attachments from backend */}
                {uploadedAttachments.map((att, index) => (
                  <tr key={`uploaded-${index}`}>
                    <td className="border px-4 py-2">{att.descricao || 'Sem descrição'}</td>
                    <td className="border px-4 py-2">{att.tipo_arquivo || 'Arquivo Anexado'}</td>
                    <td className="border px-4 py-2">{new Date(att.updated_at).toLocaleString()}</td>
                    <td className="border px-4 py-2">
                      <button onClick={() => window.open(att.publicURL)} className="text-blue-600 mr-2">Ver</button>
                      {/* Optionally add delete button here */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end">
              <button onClick={() => setIsModalOpen(false)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md mr-2">Fechar</button>
              <button onClick={handleConcluir} className="bg-green-600 text-white px-4 py-2 rounded-md">Concluir</button>
            </div>
          </div>
        </div>
      )}

      {isViewModalOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white p-6 rounded shadow-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
          <h2 className="text-2xl font-bold mb-6 border-b pb-2">Resumo do pedido</h2>
          <button
            onClick={() => setIsViewModalOpen(false)}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
            aria-label="Fechar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {viewQuotationData && viewQuotationData.data ? (() => {
            const formData = viewQuotationData.data;
            // Calcula Total Over para converter indicadores % em moeda no Step 7
            const totalOver = formData.items ? formData.items.reduce((sum, item) => {
              const valorTabela = item.valorTabela || 0;
              const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
              return sum + over * (item.quantidade || 0);
            }, 0) : 0;
            return (
              <div className="space-y-6">
                <div className="bg-gray-100 p-4 rounded-md space-y-4">
                  <div>
                    <h3 className="font-semibold">Orçamento</h3>
                    <p>Tipo de venda: {formData.tipoVendaMaterial ? 'Material' : ''}  {formData.tipoVendaServico ? '/ Serviço' : ''}</p>
                    <p>Vendedor: {formData.vendedor}</p>
                    <p>Filial: {formData.filial}</p>
                    <p>Indicador(es):</p>
                    <ul className="list-disc list-inside">
                      {formData.indicadores && formData.indicadores.length > 0 ? (
                        formData.indicadores.map((ind, idx) => {
                          const v = parseFloat(ind.valor);
                          const valorNum = isNaN(v) ? 0 : v;
                          let displayValor = '';
                          if (ind.valorTipo === '%') {
                            const deduction = totalOver * (valorNum / 100);
                            displayValor = formatBRL(deduction);
                          } else {
                            // Mantém em moeda quando já for R$
                            displayValor = formatBRL(valorNum);
                          }
                          return (
                            <li key={idx}>
                              {ind.nome} - {displayValor}
                            </li>
                          );
                        })
                      ) : (
                        <li>Nenhum indicador adicionado.</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold">Cliente</h3>
                    <p>Cliente: {formData.cliente}</p>
                    <p>Endereço de entrega: {(() => {
                      const isDifferent = formData.cidadeDiferente === 'Sim';
                      const cidade = isDifferent ? formData.cidadeEntregaDiferente : formData.cidade;
                      const estado = isDifferent ? formData.estadoEntregaDiferente : formData.estado;
                      const logradouro = isDifferent ? formData.logradouroEntregaDiferente : formData.logradouro;
                      const numero = isDifferent ? formData.numeroEntregaDiferente : formData.numero;
                      const bairro = isDifferent ? formData.bairroEntregaDiferente : formData.bairro;
                      if (cidade && estado && logradouro && numero && bairro) {
                        return `${cidade}/ ${estado}, ${logradouro}, ${numero} - ${bairro}`;
                      }
                      return 'Não informado';
                    })()}</p>
                    <p>Contribuinte de ICMS: {formData.contribuinteICMS}</p>
                    <p>Comprador: {formData.comprador}</p>
                    <p>Ordem de compra: {formData.ordemCompra}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Forma de Pagamento</h3>
                    <p>Condição de pagamento: {formData.condicaoPagamento}</p>
                    <p>Forma de pagamento: {formData.formaPagamento}</p>
                    <p>Contra aviso: {formData.contraAviso}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Frete</h3>
                    <p>Tipo de frete: {formData.tipoFrete}</p>
                    {(() => {
                      const freteDestacado = Number(formData.freteDestacadoCalculado) || 0;
                      const freteEmbutido = Number(formData.freteEmbutidoCalculado) || 0;
                      const valorFreteTotal = freteDestacado + freteEmbutido;
                      return (
                        <>
                          <p>Valor do frete: {formatBRL(valorFreteTotal)}</p>
                          <p>Frete destacado: {formatBRL(freteDestacado)}</p>
                          <p>Frete embutido: {formatBRL(freteEmbutido)}</p>
                        </>
                      );
                    })()}
                    <p>Data prevista de embarque: {formatDate(formData.dataPrevistaEmbarque)}</p>
                    <p>Observação: {formData.observacao || ''}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Itens</h3>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qtd</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Un.</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Over</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor Líquido</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frete Unitário</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor de venda</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {formData.items?.map((item) => (
                          <tr key={item.id}>
                            <td className="px-6 py-4 whitespace-nowrap">{item.produto}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{item.quantidade.toFixed(3).replace('.', ',')}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{item.unidade}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {item.over1Type === '%' ? `${item.over1 || 0}%` : `R$ ${(item.over1 || 0).toFixed(2)}`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              R$ {(() => {
                                const valorTabela = item.valorTabela || 0;
                                const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
                                return (valorTabela + over).toFixed(2);
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              R$ {(() => {
                                if (item.freteEmbutidoItem) {
                                  return (item.freteEmbutidoItem / (item.quantidade || 1)).toFixed(2);
                                }
                                return '0.00';
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              R$ {(() => {
                                const valorTabela = item.valorTabela || 0;
                                const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
                                const valorLiquido = valorTabela + over;
                                const freteUnitario = item.freteEmbutidoItem ? item.freteEmbutidoItem / (item.quantidade || 1) : 0;
                                return (valorLiquido + freteUnitario).toFixed(2);
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">{formatBRL(item.valorTotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Valor total do Pedido */}
                  <div className="mt-6 bg-gray-100 p-4 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Valor total do Pedido:</span>
                      <span className="text-lg font-bold text-green-600">
                        R$ {(() => {
                          const itemsTotal = formData.items ? formData.items.reduce((sum, item) => sum + (item.valorTotal || 0), 0) : 0;
                          const freightToAdd = formData.tipoFrete === 'CIF' ? (formData.freteDestacadoCalculado || 0) : 0;

                          // Calculate discount
                          const totalOver = formData.items ? formData.items.reduce((sum, item) => {
                            const valorTabela = item.valorTabela || 0;
                            const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
                            return sum + over * (item.quantidade || 0);
                          }, 0) : 0;

                          let descontoAmount = 0;
                          if (formData.desconto && formData.desconto > 0) {
                            if (formData.descontoType === '%') {
                              descontoAmount = totalOver * (parseFloat(formData.desconto) / 100);
                            } else {
                              descontoAmount = parseFloat(formData.desconto) || 0;
                            }
                          }

                          return (itemsTotal + freightToAdd - descontoAmount).toFixed(2).replace('.', ',');
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })() : (
            <p>Sem dados para exibir.</p>
          )}
        </div>
      </div>
      )}

      {/* Cancel Modal */}
      {isCancelModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded shadow-lg max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4 text-blue-600 border-l-4 border-blue-600 pl-3">
              Indicador Não Venda
            </h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo de Não Venda *
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Descreva o motivo da não venda..."
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                required
              />
             
            </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={handleCancelModalClose}
                className="px-6 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500 transition"
              >
                Fechar
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={!cancelReason.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Modal */}
      {isSearchModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-6 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Filtrar</h2>
              <button
                onClick={() => setIsSearchModalOpen(false)}
                className="text-gray-600 hover:text-gray-900"
                aria-label="Fechar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Número and Versão */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Número</label>
                  <input
                    type="text"
                    value={searchFilters.numero}
                    onChange={(e) => handleSearchFilterChange('numero', e.target.value)}
                    placeholder="nº"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Versão</label>
                  <select
                    value={searchFilters.versao}
                    onChange={(e) => handleSearchFilterChange('versao', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Atual">Atual</option>
                    <option value="Todas">Todas</option>
                  </select>
                </div>
              </div>

              {/* Data */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data</label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                  <input
                    type="date"
                    value={searchFilters.dataInicio}
                    onChange={(e) => handleSearchFilterChange('dataInicio', e.target.value)}
                    placeholder="Início"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span>até</span>
                  <input
                    type="date"
                    value={searchFilters.dataFim}
                    onChange={(e) => handleSearchFilterChange('dataFim', e.target.value)}
                    placeholder="Fim"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Filial */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filial</label>
                <select
                  value={searchFilters.filial}
                  onChange={(e) => handleSearchFilterChange('filial', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione</option>
                  {filiais.map(filial => (
                    <option key={filial.id} value={filial.filial}>
                      {filial.filial}
                    </option>
                  ))}
                </select>
              </div>

              {/* Setor and Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Setor</label>
                  <input
                    type="text"
                    value={searchFilters.setor}
                    onChange={(e) => handleSearchFilterChange('setor', e.target.value)}
                    placeholder="Selecione"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={searchFilters.status}
                    onChange={(e) => handleSearchFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione</option>
                    <option value="Em Negociação">Em Negociação</option>
                    <option value="Aprovado">Aprovado</option>
                    <option value="Cancelado">Cancelado</option>
                  </select>
                </div>
              </div>

              {/* Vendedor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vendedor</label>
                <input
                  type="text"
                  value={searchFilters.vendedor}
                  onChange={(e) => handleSearchFilterChange('vendedor', e.target.value)}
                  placeholder="Código ou nome do representante"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cliente</label>
                <input
                  type="text"
                  value={searchFilters.cliente}
                  onChange={(e) => handleSearchFilterChange('cliente', e.target.value)}
                  placeholder="Código ou nome do cliente"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center mt-6">
                <button
                  onClick={applyFilters}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
                >
                  <span>Pesquisar</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={clearFilters}
                  className="text-blue-600 hover:text-blue-800 text-sm underline"
                >
                  Limpar filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default QuotationsConsultar;
